/**
 * AbilityOS Intelligent Country, Language & Currency Auto-Detection Engine
 * 
 * Auto-detects the user's localized currency based on:
 * 1. User manual selection (localStorage: 'abilityos_currency')
 * 2. Active i18n language ('hi', 'mr' -> INR with 70% PPP discount)
 * 3. System timezone (Intl.DateTimeFormat().resolvedOptions().timeZone)
 * 4. Browser locale tags (navigator.languages)
 * 5. Fallback default: USD
 */

export type CurrencyCode = 'USD' | 'INR' | 'EUR' | 'GBP' | 'AUD';

export interface CurrencyConfig {
  code: CurrencyCode;
  symbol: string;
  name: string;
  flag: string;
  pppDiscountText?: string;
  countryName: string;
}

export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    flag: '🇮🇳',
    countryName: 'India & South Asia',
    pppDiscountText: '70% Purchasing Power Parity (PPP) Discount Applied'
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    flag: '🇺🇸',
    countryName: 'United States & Global',
    pppDiscountText: 'Standard Commercial Tier'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    flag: '🇪🇺',
    countryName: 'European Union',
    pppDiscountText: 'EU VAT Compliant Tier'
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    flag: '🇬🇧',
    countryName: 'United Kingdom',
    pppDiscountText: 'NHS & School District Tier'
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    flag: '🇦🇺',
    countryName: 'Australia & Oceania',
    pppDiscountText: 'NDIS Registered Provider Tier'
  }
};

export interface PlanPricing {
  monthly: number;
  annualMonthlyRate: number; // monthly equivalent when billed annually
  annualTotal: number;
}

export const TIER_PRICING: Record<CurrencyCode, {
  community: PlanPricing;
  family: PlanPricing;
  clinician: PlanPricing;
  school: PlanPricing;
}> = {
  INR: {
    community: { monthly: 0, annualMonthlyRate: 0, annualTotal: 0 },
    family: { monthly: 299, annualMonthlyRate: 249, annualTotal: 2999 },
    clinician: { monthly: 1499, annualMonthlyRate: 1249, annualTotal: 14999 },
    school: { monthly: 6999, annualMonthlyRate: 5833, annualTotal: 69999 }
  },
  USD: {
    community: { monthly: 0, annualMonthlyRate: 0, annualTotal: 0 },
    family: { monthly: 9.99, annualMonthlyRate: 7.42, annualTotal: 89 },
    clinician: { monthly: 39, annualMonthlyRate: 29.08, annualTotal: 349 },
    school: { monthly: 149, annualMonthlyRate: 108.25, annualTotal: 1299 }
  },
  EUR: {
    community: { monthly: 0, annualMonthlyRate: 0, annualTotal: 0 },
    family: { monthly: 8.99, annualMonthlyRate: 6.58, annualTotal: 79 },
    clinician: { monthly: 35, annualMonthlyRate: 26.58, annualTotal: 319 },
    school: { monthly: 139, annualMonthlyRate: 99.92, annualTotal: 1199 }
  },
  GBP: {
    community: { monthly: 0, annualMonthlyRate: 0, annualTotal: 0 },
    family: { monthly: 7.99, annualMonthlyRate: 5.75, annualTotal: 69 },
    clinician: { monthly: 29, annualMonthlyRate: 22.42, annualTotal: 269 },
    school: { monthly: 119, annualMonthlyRate: 83.25, annualTotal: 999 }
  },
  AUD: {
    community: { monthly: 0, annualMonthlyRate: 0, annualTotal: 0 },
    family: { monthly: 14.99, annualMonthlyRate: 10.75, annualTotal: 129 },
    clinician: { monthly: 59, annualMonthlyRate: 41.58, annualTotal: 499 },
    school: { monthly: 219, annualMonthlyRate: 158.25, annualTotal: 1899 }
  }
};

/**
 * Detect the default currency based on priority signals
 */
export function detectDefaultCurrency(currentLanguage?: string): CurrencyCode {
  // 1. Check user manual override in localStorage
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('abilityos_currency') as CurrencyCode | null;
    if (saved && CURRENCIES[saved]) {
      return saved;
    }
  }

  // 2. Check active i18n language
  if (currentLanguage) {
    const lang = currentLanguage.toLowerCase();
    if (lang === 'hi' || lang === 'mr' || lang.startsWith('hi-') || lang.startsWith('mr-')) {
      return 'INR';
    }
  }

  // 3. Timezone detection
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        if (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta') return 'INR';
        if (tz === 'Europe/London') return 'GBP';
        if (tz.startsWith('Australia/')) return 'AUD';
        if (
          tz.startsWith('Europe/') && 
          !tz.includes('London') && 
          !tz.includes('Dublin')
        ) {
          return 'EUR';
        }
        if (tz.startsWith('America/')) return 'USD';
      }
    } catch {
      // Fallback
    }
  }

  // 4. Browser languages
  if (typeof navigator !== 'undefined' && navigator.languages) {
    for (const l of navigator.languages) {
      const langUpper = l.toUpperCase();
      if (langUpper.includes('IN') || langUpper.includes('HI') || langUpper.includes('MR')) {
        return 'INR';
      }
      if (langUpper.includes('GB')) return 'GBP';
      if (langUpper.includes('AU')) return 'AUD';
      if (
        langUpper.includes('DE') || 
        langUpper.includes('FR') || 
        langUpper.includes('ES') || 
        langUpper.includes('IT') || 
        langUpper.includes('NL')
      ) {
        return 'EUR';
      }
    }
  }

  // 5. Default Fallback
  return 'USD';
}

/**
 * Format price according to currency rules
 */
export function formatPriceString(amount: number, currency: CurrencyCode): string {
  const config = CURRENCIES[currency];
  if (amount === 0) return `${config.symbol}0`;
  
  if (currency === 'INR') {
    // Indian comma formatting
    return `${config.symbol}${amount.toLocaleString('en-IN')}`;
  }
  
  if (Number.isInteger(amount)) {
    return `${config.symbol}${amount.toLocaleString('en-US')}`;
  }
  
  return `${config.symbol}${amount.toFixed(2)}`;
}
